import type { Recommendation } from "../types/recommendation";

/**
 * Mock `Recommendation` fixture for local dev / component tests / the golden
 * fixture render test — NOT real data. Deliberately vertical-neutral (no
 * protest-only or BPO-only field naming) since the result screen renders the
 * same contract across all three verticals per the project's standing rule
 * ("never build as if one vertical is primary"). Exercises all five tiers,
 * both comp types, a disclosed item, and a gap, so the renderer's full path
 * is covered by one fixture.
 */
export const mockRecommendation: Recommendation = {
  subjectId: "subj_mock_0001",
  scenario: "as-is",
  fields: {
    indicated_value: {
      id: "indicated_value",
      label: "Indicated value",
      display: "$1,792,000",
      tier: "verified",
      source: "Sales comparison approach (this analysis)",
      state: "computed",
      how: "Weighted average of the three ranked sales comps' adjusted price/SF, applied to subject building SF.",
      formula: "sum(comp.adj_psf * comp.weight) * subject.building_sf",
      inputs: [
        { label: "Comp #1 adjusted $/SF", ref: "comp_1_adj_psf" },
        { label: "Comp #2 adjusted $/SF", ref: "comp_2_adj_psf" },
        { label: "Subject building SF", ref: "subject_building_sf" },
      ],
      rows: [
        { label: "Subject — 7519 Spicewood Dr", value: "18,420 SF", subject: true },
        { label: "Comp #1 — 4310 Legacy Dr", value: "$97.20/SF" },
        { label: "Comp #2 — 891 Coit Rd", value: "$101.40/SF" },
        { label: "Comp #3 — 2200 Redbud Blvd", value: "$94.80/SF" },
      ],
    },
    subject_building_sf: {
      id: "subject_building_sf",
      label: "Subject building SF",
      display: "18,420 SF",
      tier: "entered",
      source: "You entered",
    },
    asking_reference: {
      id: "asking_reference",
      label: "Nearby asking price (reference only)",
      display: "$115.00/SF",
      tier: "indicative",
      source: "LoopNet listing #48213 (active, not closed)",
      reason: "An asking price is indicative regardless of platform — closed-only sales are verified.",
    },
    cap_rate_assumption: {
      id: "cap_rate_assumption",
      label: "Cap rate (income approach)",
      display: "6.75%",
      tier: "assumption",
      source: "Market survey range, this submarket (CBRE Cap Rate Survey, H1 2026)",
      reason: "No income-approach comp closed in the trailing 12 months for this property type in this submarket.",
    },
    prior_permit_value: {
      id: "prior_permit_value",
      label: "Most recent permitted improvement value",
      display: "—",
      tier: "gap",
      source: "Not found",
      reason: "County permit portal returned no records for this parcel in the last 10 years.",
    },
  },
  salesComps: [
    {
      rank: 1,
      addr: "4310 Legacy Dr",
      sf: "17,900 SF",
      yr: 2011,
      dist: "0.6 mi",
      basis: "Closed sale",
      match: 88,
      priceRef: "indicated_value",
      psfRef: "indicated_value",
      good: "Same submarket, similar age and construction class, closed within 6 months.",
      notnot: "Slightly smaller floor plate; corner-lot premium not adjusted for.",
    },
    {
      rank: 2,
      addr: "891 Coit Rd",
      sf: "19,050 SF",
      yr: 2014,
      dist: "1.1 mi",
      basis: "Closed sale",
      match: 81,
      good: "Comparable tenant mix and site coverage ratio.",
      notnot: "Newer construction (3 yrs) than subject; no explicit age adjustment applied.",
    },
  ],
  rentComps: [
    {
      rank: 1,
      addr: "2200 Redbud Blvd",
      sf: "16,500 SF",
      dist: "1.4 mi",
      basis: "Signed lease, 2025",
      match: 74,
      rateRef: "cap_rate_assumption",
      good: "Same use class, comparable parking ratio.",
      notnot: "Triple-net vs subject's modified-gross history — not normalized in this pass.",
    },
  ],
  disclosed: [
    {
      name: "1420 Custer Rd (excluded)",
      metric: "$142/SF asking",
      kind: "Asking only, not closed",
      reason: "Not used in the conclusion — no closed sale confirms this level; shown so the record is not silent about it.",
      source: "CoStar listing, pulled 2026-07-20",
      tier: "indicative",
    },
  ],
  gaps: [
    {
      ref: "prior_permit_value",
      label: "Most recent permitted improvement value",
      reason: "County permit portal returned no records for this parcel in the last 10 years.",
    },
  ],
  evidenceIndex: { support: 75, disclosed: 25 },
  generatedAt: "2026-07-23T00:00:00Z",
  pipelineVersion: "next-mock-0.1.0",
};
